/**
 * The character-tier pinyin table, and the lookup `RunContext.pinyin` expects.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────
 * WHAT THIS IS HONEST ABOUT, because the copy on the page has to say it too.
 *
 * This is a CHARACTER table. There is no phrase dictionary and no segmenter, so a character
 * with more than one reading gets its most frequent one and nothing looks at the surrounding
 * words. Measured against `kHanyuPinlu`'s corpus counts over 2,191,752 occurrences, that is
 * **wrong on 3.83% of character occurrences** — and the errors are not a long tail of rare
 * characters, they are the short head:
 *
 *   地  de(7394) dì(4976)              40.2% of its uses need the non-primary reading
 *   为  wèi(3962) wéi(2433)            38.0%
 *   得  de(5096) dé(1496) děi(637)     29.5%
 *   过  guò(5174) guo(1222)            19.1%
 *   个  gè(11693) ge(1891)             14.0%
 *
 * 3.83% is a FLOOR for subtitles rather than a ceiling: that corpus is general modern Chinese,
 * and the particles carrying the error — 得, 地, 着, 了, 个 — are more frequent in dialogue than
 * in prose. The tool says so on the page. A pinyin tool that is quietly wrong on 地 in the middle
 * of ordinary speech is worse than one that says which cases it cannot resolve.
 *
 * The tier that would fix it is a segmenter plus a phrase dictionary, not a phrase list: 地/得/过
 * are decided by SYNTACTIC ROLE, which no lookup can answer.
 * ─────────────────────────────────────────────────────────────────────────────────────
 *
 * THE STORED FORM IS COMPACT AND IS EXPANDED HERE. `table.json` maps a character to its readings
 * as one comma-joined string; the wire shape in `adapter.md` is
 * `{text, tone, sourceLength}[]`. Storing the wire shape directly costs 1.4 MB instead of 311 KB
 * for exactly the same information — `sourceLength` is 1 for every entry in a character table,
 * and `tone` is derivable from the syllable. Expanding once at load is cheaper than shipping
 * the redundancy.
 *
 * READINGS ARE ORDERED MOST-FREQUENT-FIRST, which is not the order the source ships. See
 * `build-table.py`: taking the dictionary's first reading would give 地 → dì and 得 → dé, both
 * the LESS common reading, on the two characters that carry the most error.
 *
 * ATTRIBUTION IS AN OBLIGATION, NOT A COURTESY — see `NOTICE.md`. The Unicode licence asks for
 * nothing else, and the notice has to reach users rather than only the repository.
 */

/** The line the tool page must carry. Attribution that never leaves the repo is not attribution. */
export const PINYIN_ATTRIBUTION =
  'Pinyin readings derived from the Unicode Character Database, © 2025 Unicode®, Inc., ' +
  'used under the Unicode License v3.';

/** What the page must tell the user about accuracy, in the founder-ruled framing. */
export const PINYIN_ACCURACY_NOTE =
  'Readings are per character. Chinese characters with more than one reading — 地, 得, 为, 过, ' +
  '个 and others — are given their most common one, so some will need checking.';

export interface Reading {
  readonly text: string;
  readonly tone: number;
  readonly sourceLength: number;
}

/** The stored form: character → comma-joined readings, most frequent first. */
export type CompactTable = Readonly<Record<string, string>>;

/**
 * Tone from the syllable's own diacritic, 1–4, or 5 for neutral.
 *
 * NFD is what makes this a lookup rather than a table of every marked vowel: decomposed, the
 * mark is its own code point regardless of which vowel carries it. The alternative — enumerating
 * the marked vowels by hand — is exactly the bug that put a 16.08% error rate in a report of mine
 * before I noticed the enumeration had dropped two of them.
 */
const TONE_MARKS: Readonly<Record<string, number>> = {
  '̄': 1, // macron
  '́': 2, // acute
  '̌': 3, // caron
  '̀': 4, // grave
};

export function toneOf(syllable: string): number {
  for (const ch of syllable.normalize('NFD')) {
    const t = TONE_MARKS[ch];
    if (t !== undefined) return t;
  }
  return 5;
}

/** Strips the tone diacritic, leaving the bare syllable: `nǐ` → `ni`. */
export function stripTone(syllable: string): string {
  let out = '';
  for (const ch of syllable.normalize('NFD')) {
    if (TONE_MARKS[ch] === undefined) out += ch;
  }
  return out.normalize('NFC');
}

/** `nǐ` → `ni3`. Neutral tone takes no digit, which is the convention readers expect. */
export function toNumbered(syllable: string): string {
  const tone = toneOf(syllable);
  const bare = stripTone(syllable);
  return tone === 5 ? bare : `${bare}${tone}`;
}

export function expandEntry(readings: string): readonly Reading[] {
  return readings.split(',').map((text) => ({ text, tone: toneOf(text), sourceLength: 1 }));
}

/**
 * Builds the `RunContext.pinyin` lookup from the compact table.
 *
 * IT RETURNS ONE READING PER CHARACTER AND SKIPS EVERYTHING ELSE. Latin text, punctuation and
 * spaces are not Han and get no entry, so `sourceLength` sums to the length of the HAN it
 * covered rather than of the input — the caller pairs readings to characters by walking both.
 *
 * A character absent from the table yields no reading rather than a placeholder. A placeholder
 * would be a reading the tool invented.
 */
export function createLookup(table: CompactTable): (text: string) => readonly Reading[] {
  return (text: string) => {
    const out: Reading[] = [];
    for (const ch of text) {
      const entry = table[ch];
      if (entry === undefined) continue;
      const first = entry.split(',', 1)[0] as string;
      out.push({ text: first, tone: toneOf(first), sourceLength: 1 });
    }
    return out;
  };
}
